import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Container,
  SimpleGrid,
  Card,
  CardBody,
  Flex,
} from '@chakra-ui/react';
import ExtensionDemo from '../components/ExtensionDemo';

// ── Install CTA ───────────────────────────────────────────────────────────────
const WaitlistForm = () => {
  return (
    <Box display="flex" justifyContent="center">
      <Box w="100%" maxW="560px">
        <Text textAlign="center" fontSize="sm" color="gray.500" mb={3}>
          The extension is live — add it to Chrome and clean up your inbox today.
        </Text>
        <Flex justifyContent="center">
          <Button
            as="a"
            href="https://chromewebstore.google.com/detail/email-cleaner/gidoimfcmopgaaneodgmdcmbdaeekboo"
            target="_blank"
            rel="noopener noreferrer"
            bg="#52c41a"
            color="white"
            size="lg"
            px={8}
            _hover={{ bg: '#389e0d' }}
            transition="background-color 0.2s"
          >
            Add to Chrome
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <VStack w="full" spacing={0} bg="gray.50">
      {/* Combined Image + Text Block */}

      {/* Desktop Layout - Text left, animated demo right */}
      <Box
        w="full"
        display={{ base: "none", lg: "flex" }}
        bg="gray.50"
        minH={{ lg: "520px", xl: "600px" }}
      >
        <Container maxW="1400px" py={16}>
          <Flex align="center" gap={10} justify="space-between">
            {/* Text Content */}
            <VStack spacing={4} align="start" textAlign="left" maxW={{ lg: "420px", xl: "460px" }} flexShrink={0} pl={{ lg: 8, xl: 14 }}>
              <Heading
                size="3xl"
                color="gray.900"
                fontWeight="normal"
                lineHeight="1.15"
                letterSpacing="-0.01em"
              >
                EMAIL CLEANER
              </Heading>
              <Heading
                size="xl"
                color="gray.900"
                fontWeight="normal"
                lineHeight="1.2"
              >
                Take Back Your Inbox
              </Heading>
              <Text
                fontSize="lg"
                color="gray.600"
                lineHeight="relaxed"
              >
                A Chrome extension that automatically cleans up unwanted emails in Gmail, so you never have to think about them again.
              </Text>
            </VStack>

            {/* Animated Demo — isolation keeps internal z-indices below the sticky header */}
            <Flex flex={1} justify="center" align="center" sx={{ isolation: 'isolate' }}>
              <ExtensionDemo />
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Layout - Text on top, animated demo below */}
      <VStack
        w="full"
        spacing={0}
        display={{ base: "flex", lg: "none" }}
      >
        {/* Text Section */}
        <Box w="full" bg="gray.50" pt={8} pb={6} px={6}>
          <VStack spacing={4} align="center" textAlign="center">
            <Heading
              size={{ base: "xl", md: "2xl" }}
              color="gray.900"
              fontWeight="normal"
              lineHeight="1.15"
              letterSpacing="-0.01em"
            >
              EMAIL CLEANER
            </Heading>
            <Heading
              size={{ base: "md", md: "lg" }}
              color="gray.900"
              fontWeight="normal"
              lineHeight="1.2"
            >
              Take Back Your Inbox
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              lineHeight="relaxed"
              textAlign="center"
            >
              A Chrome extension that automatically cleans up unwanted emails in Gmail, so you never have to think about them again.
            </Text>
          </VStack>
        </Box>

        {/* Demo Section */}
        <Box w="full" bg="gray.50" px={4} pb={6} display="flex" justifyContent="center" sx={{ isolation: 'isolate' }}>
          <ExtensionDemo />
        </Box>
      </VStack>

      {/* CTA Button */}
      <Container maxW="container.xl" pt={{ base: 8, lg: "64px" }} pb={{ base: 8, lg: "64px" }} bg="gray.50">
        <WaitlistForm />
      </Container>
    </VStack>
  );
};

// About Section Component
const AboutSection = () => {
  return (
    <Box bg="gray.50" pt={{ base: 0, lg: 0 }} pb={{ base: 8, lg: 20 }}>
      <Container maxW="container.xl">
        <VStack spacing={16}>
          {/* Two Single Large Cards Side by Side */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} w="full">
            {/* Left Card - How It Works */}
            <Card
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderTop="2px solid #52c41a"
              shadow="sm"
              transition="transform 0.2s, box-shadow 0.2s"
              _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
              h="full"
            >
              <CardBody>
                <VStack spacing={8} w="full">
                  <Heading
                    size="lg"
                    color="#135200"
                    fontWeight="normal"
                    textAlign="center"
                  >
                    How It Works
                  </Heading>

                  <VStack spacing={6} w="full">
                    {[
                      {
                        step: "Step 1",
                        title: "Install the extension and connect your Gmail account.",
                      },
                      {
                        step: "Step 2",
                        title: "Review the cleanup rules suggested for your inbox.",
                      },
                      {
                        step: "Step 3",
                        title: "Activate rules and let it handle the rest.",
                      }
                    ].map((item) => (
                      <VStack key={item.step} spacing={1} align="start" w="full">
                        <Text fontWeight="bold" color="#135200" fontSize="lg">
                          {item.step}
                        </Text>
                        <Text color="gray.600" lineHeight="relaxed">
                          {item.title}
                        </Text>
                      </VStack>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Right Card - Why Email Cleaner */}
            <Card
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderTop="2px solid #52c41a"
              shadow="sm"
              transition="transform 0.2s, box-shadow 0.2s"
              _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
              h="full"
            >
              <CardBody>
                <VStack spacing={8} w="full">
                  <Heading
                    size="lg"
                    color="#135200"
                    fontWeight="normal"
                    textAlign="center"
                  >
                    Why Email Cleaner
                  </Heading>

                  <VStack spacing={6} w="full">
                    {[
                      {
                        title: "Smart Suggestions",
                        description: "Email Cleaner detects new senders and suggests cleanup rules tailored to your inbox.",
                      },
                      {
                        title: "You Are Always in Control",
                        description: "Nothing is ever deleted without your approval. Review, accept, or dismiss every rule.",
                      },
                      {
                        title: "Privacy-First Design",
                        description: "Runs entirely in your browser. No servers, no cloud storage. Your email stays private.",
                      }
                    ].map((feature) => (
                      <VStack key={feature.title} spacing={1} align="start" w="full">
                        <Text fontWeight="bold" color="#135200" fontSize="lg">
                          {feature.title}
                        </Text>
                        <Text color="gray.600" lineHeight="relaxed">
                          {feature.description}
                        </Text>
                      </VStack>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

// Home Page Component
const Home = () => {
  return (
    <Box as="main" minH="100vh" bg="gray.50">
      <HeroSection />
      <AboutSection />
    </Box>
  );
};

export default Home;
